import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, ExternalLink, Newspaper } from 'lucide-react';
import { storiaTimeline, notizieInEvidenza, siteInfo } from '../data/siteData';

export const Storia = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="storia-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#002060] to-[#003380] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla home
          </Link>
          
          <div className="text-center">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5CB9E5] mb-4 block">
              Chi Siamo
            </span>
            <h1 className="font-serif text-4xl md:text-6xl font-bold text-white mb-6">
              La nostra Storia
            </h1>
            <p className="text-white/80 max-w-3xl mx-auto text-lg">
              Dal 2020, un percorso di impegno civico per la rinascita della Valle d'Aosta
            </p>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 bg-[#FFD700]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <blockquote className="font-serif text-xl md:text-2xl text-[#002060] italic leading-relaxed">
            "Vogliamo rappresentare un nuovo modo di fare politica. Crediamo che l'approccio del passato non sia più proficuo: oggi più che mai è importante e necessario aprirsi a nuove opportunità, saperle cogliere e portarle avanti con fiducia e onestà."
          </blockquote>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <img 
              src={siteInfo.logo}
              alt={siteInfo.name}
              className="w-24 h-24 mx-auto mb-6 rounded-full border-4 border-[#5CB9E5] shadow-lg"
            />
            <p className="text-slate-700 text-lg leading-relaxed">
              <strong className="text-[#002060]">La Renaissance Valdôtaine</strong> – prima Rinascimento Valle d'Aosta – nasce come movimento civico nel 2020. Un gruppo di persone, stanche e sfiduciate dal sistema politico valdostano, decidono di unirsi per seguire un progetto volto a una rinascita della città di Aosta e della regione Valle d'Aosta.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 bg-[#F4F6F8]" data-testid="timeline-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#002060] text-center mb-12">
            Il nostro percorso
          </h2>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-[#5CB9E5] transform md:-translate-x-1/2"></div>

            {storiaTimeline.map((evento, index) => (
              <div 
                key={evento.data}
                className={`relative flex flex-col md:flex-row gap-4 md:gap-8 mb-12 ${
                  index % 2 === 0 ? 'md:flex-row-reverse' : ''
                }`}
                data-testid={`timeline-item-${index}`}
              >
                {/* Timeline dot */}
                <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-[#FFD700] rounded-full border-4 border-white shadow transform -translate-x-1/2 z-10"></div>
                
                {/* Content */}
                <div className={`ml-12 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                  <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-[#5CB9E5]">
                    <div className="flex items-center gap-2 text-[#5CB9E5] mb-2">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm font-semibold">{evento.data}</span>
                    </div>
                    <h3 className="font-serif text-xl font-bold text-[#002060] mb-2">
                      {evento.titolo}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {evento.descrizione}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Notizie in Evidenza */}
      <section className="py-16 bg-white" data-testid="news-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Newspaper className="w-6 h-6 text-[#5CB9E5]" />
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5CB9E5]">
                Rassegna Stampa
              </span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#002060]">
              Notizie in evidenza
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {notizieInEvidenza.map((notizia) => (
              <a
                key={notizia.nome}
                href={notizia.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-4 bg-[#F4F6F8] rounded-lg hover:bg-[#002060] hover:text-white group transition-colors"
                data-testid={`news-link-${notizia.nome.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className="font-medium">{notizia.nome}</span>
                <ExternalLink className="w-4 h-4 text-[#5CB9E5] group-hover:text-[#FFD700]" />
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
